import { useCallback } from 'react';
import { getAccessToken } from 'utils/auth';

function getToken(): string {
  return getAccessToken();
}

function getBaseUrl(): string {
  const domain =
    process.env.REACT_APP_SUPPORT_API_DOMAIN || process.env.REACT_APP_DOMAIN || 'hijackpoker.com';
  return `https://support-api.${domain}`;
}

function getPlayerGUID(): string {
  return localStorage.getItem('playerGUID') || '';
}

async function api(path: string, options: RequestInit = {}) {
  const jwt = getToken();
  const res = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
      'X-Player-GUID': getPlayerGUID(),
      ...options.headers
    }
  });
  return res.json();
}

export type Ticket = {
  ticketId: string;
  playerGUID: string;
  playerName: string;
  status: string;
  category: string;
  subject: string;
  priority: string;
  assignedAgent: string;
  agentName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  messageCount: number;
};

export type Message = {
  messageId: string;
  ticketId: string;
  sender: 'player' | 'agent' | 'system';
  senderName: string;
  body: string;
  createdAt: string;
  attachmentId?: string;
  attachmentName?: string;
  attachmentType?: string;
  attachmentUrl?: string;
  // Discriminator for system messages the client should render specially
  // rather than showing the raw `body`. Currently only `'TICKET_CLOSED'` — the
  // server-side `body` intentionally includes the closing agent's real name
  // (admin console displays it verbatim), so the client-side render swaps in
  // a static "Ticket closed by HJ Agent" line instead of exposing the agent's
  // identity to the player.
  messageType?: 'TICKET_CLOSED';
  // Per-message location snapshot — where the player was in the app when
  // they sent this reply. Captured on the client at send time and echoed by
  // the admin console next to the timestamp so support can see how the
  // player was navigating during the conversation. Only present on player
  // messages (agent/system/internal messages don't carry it).
  context?: PlayerContext;
};

export type PlayerContext = {
  url?: string;
  gameID?: string;
  gameNo?: string;
  gameType?: string;
  tableNo?: string;
  tableName?: string;
  blindLevels?: string;
  hand?: string;
  view?: string;
  timestamp?: string;
};

export function useSupportApi() {
  const createTicket = useCallback(
    async (category: string, message: string, subject?: string, context?: PlayerContext) => {
      return api('/tickets', {
        method: 'POST',
        body: JSON.stringify({ category, message, subject, context })
      });
    },
    []
  );

  const getTickets = useCallback(async (status?: string) => {
    const params = status ? `?status=${status}` : '';
    return api(`/tickets${params}`);
  }, []);

  const getTicket = useCallback(async (ticketId: string) => {
    return api(`/tickets/${ticketId}`);
  }, []);

  // Server hard-caps at 100 (see support-api/functions/getMessages/handler.js:
  // `Math.min(parseInt(limitStr) || 50, 100)`), so this default asks for the
  // maximum window the server will return in one call. Doubles the amount of
  // history the client can render without any UI change; older messages beyond
  // the 100-newest still need the `before` cursor to reach.
  const getMessages = useCallback(async (ticketId: string, limit = 100) => {
    return api(`/tickets/${ticketId}/messages?limit=${limit}`);
  }, []);

  const sendMessage = useCallback(
    async (
      ticketId: string,
      message: string,
      attachment?: {
        attachmentId: string;
        attachmentName: string;
        attachmentType: string;
        attachmentKey: string;
      },
      context?: PlayerContext
    ) => {
      // Build the body explicitly so `context` (the per-message player location
      // snapshot the admin console shows next to the timestamp) is a top-level
      // key you can see in DevTools Network tab. The prior spread-based form
      // (`...(context ? { context } : {})`) was correct but harder to eyeball
      // in the outgoing payload while diagnosing.
      const payload: Record<string, unknown> = { message };
      if (attachment) {
        payload.attachmentId = attachment.attachmentId;
        payload.attachmentName = attachment.attachmentName;
        payload.attachmentType = attachment.attachmentType;
        payload.attachmentKey = attachment.attachmentKey;
      }
      if (context) {
        payload.context = context;
      }
      return api(`/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },
    []
  );

  const getUploadUrl = useCallback(
    async (ticketId: string, fileName: string, fileType: string, fileSize: number) => {
      return api(`/tickets/${ticketId}/upload`, {
        method: 'POST',
        body: JSON.stringify({ fileName, fileType, fileSize })
      });
    },
    []
  );

  const uploadFile = useCallback(
    async (ticketId: string, file: File) => {
      // 1. Get pre-signed URL
      const res = await getUploadUrl(ticketId, file.name, file.type, file.size);
      if (res?.error) throw new Error(res.error);

      // 2. Upload directly to S3
      await fetch(res.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      // 3. Return attachment metadata for sendMessage
      return {
        attachmentId: res.attachmentId,
        attachmentName: file.name,
        attachmentType: file.type,
        attachmentKey: res.key
      };
    },
    [getUploadUrl]
  );

  const reopenTicket = useCallback(async (ticketId: string) => {
    return api(`/tickets/${ticketId}/reopen`, { method: 'POST' });
  }, []);

  return {
    createTicket,
    getTickets,
    getTicket,
    getMessages,
    sendMessage,
    uploadFile,
    reopenTicket
  };
}
